import { useRef, useState } from "react";
import { type ProviderVenue } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderOffline } from "../../lib/provider-offline";
import { getProviderImageErrorMessage, uploadProviderImage } from "./provider-image-upload";

export function VenueLogoManager({
  canManage,
  onSave,
  venue,
}: {
  canManage: boolean;
  onSave: (logoUrl: string | null) => Promise<unknown>;
  venue: ProviderVenue;
}) {
  const { t } = useAppPreferences();
  const { isOnline } = useProviderOffline();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const hasLogo = Boolean(venue.logo_url) && !previewFailed;

  const handleUpload = async (file: File) => {
    setError(null);
    setIsWorking(true);
    try {
      const { signed } = await uploadProviderImage("logo", venue.id, file);
      await onSave(signed.public_url);
      setPreviewFailed(false);
    } catch (uploadError) {
      setError(getProviderImageErrorMessage(uploadError, t));
    } finally {
      setIsWorking(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setIsWorking(true);
    try {
      await onSave(null);
      setPreviewFailed(false);
    } catch (removeError) {
      setError(getProviderImageErrorMessage(removeError, t));
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="ops-subtle-surface mt-3 grid gap-3 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="ops-label">{t("Venue logo")}</p>
          <p className="mt-1 text-xs text-slate-400">
            {hasLogo
              ? t("Shown as the venue's branded mark where a logo is supported.")
              : t("No logo yet. The app shows your main photo instead.")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border border-white/10 bg-slate-950/45">
            {hasLogo ? (
              <img
                alt={t("{name} logo", { name: venue.name })}
                className="h-full w-full object-cover"
                onError={() => setPreviewFailed(true)}
                src={venue.logo_url ?? ""}
              />
            ) : (
              <span aria-hidden="true" className="text-xl text-slate-500">◎</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="ops-button"
              data-size="sm"
              data-variant="secondary"
              disabled={!canManage || !isOnline || isWorking}
              onClick={() => inputRef.current?.click()}
              title={!isOnline ? t("Image upload is unavailable while offline.") : undefined}
              type="button"
            >
              {isWorking ? t("Working...") : hasLogo ? t("Replace logo") : t("Upload logo")}
            </button>
            {venue.logo_url ? (
              <button
                className="ops-button"
                data-size="sm"
                data-variant="danger"
                disabled={!canManage || isWorking}
                onClick={() => void handleRemove()}
                type="button"
              >
                {t("Remove")}
              </button>
            ) : null}
          </div>
          <input
            accept="image/*"
            className="hidden"
            disabled={!canManage || !isOnline || isWorking}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void handleUpload(file);
            }}
            ref={inputRef}
            type="file"
          />
        </div>
      </div>
      {!canManage ? <p className="text-xs text-slate-500">{t("Only owners can change venue setup.")}</p> : null}
      {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
    </div>
  );
}
